import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelName } from '@casops/store/api';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';
import {
  AssetCertificateModel,
  useDeleteCertificateMutation,
} from '@casops/store/apis/assetCertificatesApi';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';

type Model = AssetCertificateModel;
const modelName = ModelName.Certificate;
const permission = Permission.Admin;

const getItemName = ({ an }: Model): string => an;

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  deleteProps: {
    canDeleteItem: true,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteCertificateMutation,
    getItemName,
  },
};

export function useCertificatesColDefs(): ColDef<Model>[] {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);
  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      {
        field: 'an',
        headerName: t('Fields.AssetName'),
        sort: 'asc',
        minWidth: 120,
      },
      { field: 'did', headerName: t('Fields.DeviceId'), minWidth: 180 },
      {
        field: 'ts',
        headerName: t('Fields.Timestamp'),
        minWidth: 180,
        ...absoluteTimeColDef,
      },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions, absoluteTimeColDef]);

  return columnDefs;
}

export default useCertificatesColDefs;
